import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategoriesAsync, selectCategories } from '../product/productSlice';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

// Category icons - Using local PNG images from public folder
// To add a new icon: place the PNG in public/ folder and add the path here
const categoryIconMap = {
  // Educational Platforms
  'educational platforms': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'educational': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'education': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'learning': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'courses': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'e-learning': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  
  // Tools
  'tools': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  'utilities': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  'software tools': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  
  // Music Tools
  'music': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'spotify': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'music tools': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'music platforms': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  
  // AI Tools
  'ai tools': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  'ai': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  'artificial intelligence': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  
  // VPNs
  'vpn': { icon: '🌐', color: 'bg-teal-500', image: '/vpn.png' },
  'vpns': { icon: '🌐', color: 'bg-teal-500', image: '/vpn.png' },
  
  // Streaming Platforms
  'streaming': { icon: '▶️', color: 'bg-red-500', image: '/stream-video.png' },
  'streaming platforms': { icon: '▶️', color: 'bg-red-500', image: '/stream-video.png' },
  'netflix': { icon: '🎬', color: 'bg-red-600', image: '/stream-video.png' },
  'entertainment': { icon: '🎬', color: 'bg-pink-500', image: '/stream-video.png' },
  'subscriptions': { icon: '▶️', color: 'bg-purple-500', image: '/stream-video.png' },
  
  // E-commerce
  'e-commerce': { icon: '🛒', color: 'bg-orange-500', image: '/ecommerce.png' },
  'ecommerce': { icon: '🛒', color: 'bg-orange-500', image: '/ecommerce.png' },
  
  // Other categories (using emojis as fallback)
  'microsoft office': { icon: '📄', color: 'bg-red-500', image: null },
  'windows': { icon: '🪟', color: 'bg-blue-500', image: null },
  'antivirus': { icon: '🛡️', color: 'bg-green-500', image: null },
  'adobe': { icon: '🎨', color: 'bg-red-600', image: null },
  'gaming': { icon: '🎮', color: 'bg-orange-500', image: null },
  'xbox': { icon: '🎮', color: 'bg-green-600', image: null },
  'playstation': { icon: '🎮', color: 'bg-blue-600', image: null },
  'linkedin': { icon: '💼', color: 'bg-blue-700', image: null },
  'visio': { icon: '📊', color: 'bg-blue-500', image: null },
  'project': { icon: '📋', color: 'bg-green-600', image: null },
  'autodesk': { icon: '🏗️', color: 'bg-orange-500', image: null },
  'coreldraw': { icon: '🎨', color: 'bg-green-500', image: null },
};

// Case-insensitive category lookup
const getCategoryData = (label) => {
  const lowerLabel = label?.toLowerCase() || '';
  return categoryIconMap[lowerLabel] || { icon: '📦', color: 'bg-gray-500', image: null };
};

function LeftCategorySidebar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const categories = useSelector(selectCategories);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const currentCategory = searchParams.get('category');

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchCategoriesAsync());
    }
  }, [dispatch, categories.length]);

  const handleCategoryClick = (categoryValue) => {
    navigate(`/products?category=${categoryValue}`);
    setIsMobileOpen(false);
  };

  const handleAllCategories = () => {
    navigate('/products');
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Desktop Sidebar - Fixed Left */}
      <div 
        className={`hidden lg:flex fixed left-0 top-0 h-full z-50 flex-col bg-white border-r border-gray-200 shadow-lg transition-all duration-300 ${
          isExpanded ? 'w-64' : 'w-16'
        }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Header - All Categories Button */}
        <button
          onClick={handleAllCategories}
          className={`bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center py-4 transition-colors ${isExpanded ? 'px-4' : 'px-2'}`}
        >
          <Bars3Icon className="w-6 h-6 flex-shrink-0" />
          {isExpanded && <span className="ml-3 font-semibold whitespace-nowrap">All Categories</span>}
        </button>

        {/* Category List */}
        <div className="flex-1 overflow-y-auto py-2 sidebar-scroll">
          {categories && categories.length > 0 ? categories.map((cat) => {
            const catData = getCategoryData(cat.label);
            const isActive = currentCategory === cat.value;

            return (
              <button
                key={cat.value}
                onClick={() => handleCategoryClick(cat.value)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 transition-all duration-200
                  ${isActive 
                    ? 'bg-blue-50 border-r-4 border-blue-600' 
                    : 'hover:bg-gray-50'
                  }
                `}
                title={cat.label}
              >
                {/* Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'ring-2 ring-blue-400' : ''}`}>
                  {catData.image ? (
                    <img 
                      src={catData.image} 
                      alt={cat.label}
                      className="w-6 h-6 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : (
                    <span className="text-xl">{catData.icon}</span>
                  )}
                </div>

                {/* Label - Only show when expanded */}
                {isExpanded && (
                  <span className={`text-sm truncate transition-opacity duration-200 ${
                    isActive ? 'font-semibold text-blue-600' : 'text-gray-700'
                  }`}>
                    {cat.label}
                  </span>
                )}
              </button>
            );
          }) : <div className="text-center py-4 text-gray-500">No categories available</div>}
        </div>
      </div>

      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed left-4 top-20 z-50 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
      >
        <Bars3Icon className="w-6 h-6" />
      </button>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl flex flex-col animate-slide-in">
            {/* Header - All Categories Button */}
            <button
              onClick={handleAllCategories}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-between px-4 py-4 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Bars3Icon className="w-6 h-6" />
                <span className="font-semibold">All Categories</span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setIsMobileOpen(false); }}
                className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center hover:bg-white/30"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </button>

            {/* Category List */}
            <div className="flex-1 overflow-y-auto">
              {categories.map((cat) => {
                const catData = getCategoryData(cat.label);
                const isActive = currentCategory === cat.value;

                return (
                  <button
                    key={cat.value}
                    onClick={() => handleCategoryClick(cat.value)}
                    className={`
                      w-full flex items-center gap-4 px-4 py-4 transition-all duration-200 border-b border-gray-100
                      ${isActive 
                        ? 'bg-blue-50 border-l-4 border-l-blue-600' 
                        : 'hover:bg-gray-50'
                      }
                    `}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${catData.color} bg-opacity-10`}>
                      {catData.image ? (
                        <img 
                          src={catData.image} 
                          alt={cat.label}
                          className="w-7 h-7 object-contain"
                        />
                      ) : (
                        <span className="text-2xl">{catData.icon}</span>
                      )}
                    </div>

                    {/* Label */}
                    <span className={`text-sm ${
                      isActive ? 'font-semibold text-blue-600' : 'text-gray-700'
                    }`}>
                      {cat.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Spacer for main content - Desktop only */}
      <div className="hidden lg:block w-16 flex-shrink-0" />
    </>
  );
}

export default LeftCategorySidebar;
